import { FC, Fragment, useContext, useEffect, useState } from 'react';
import {
  ChangeHotelResponse,
  DynamicEventResponse,
} from 'ts/dynamic/interfaces/DynamicResponse';
import Button from 'components/ui/Button/Button';
import { useTranslation } from 'react-i18next';
import { faArrowCircleLeft } from '@fortawesome/free-solid-svg-icons';
import {
  getFilteredHotels,
  getSortedHotels,
  initHotels,
  initSingleHotel,
} from 'utils/dynamic/dynamic-htl';
import { getDistanceByCoords, showError, showWarning } from 'utils/utils';
import { ohsForChangeHotel } from 'services/dynamic';
import { useSearchParams } from 'react-router-dom';
import { DynamicContext } from 'contexts/DynamicContext';
import FilterResults from 'components/hotel/Result/Filters/FilterResults';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';
import { HotelFilterContext } from 'contexts/HotelFilterContext';
import HotelsList from 'components/hotel/Result/Cards/HotelsList';
import { MapHotelContext } from 'contexts/MapHotelContext';
import { LocationValue } from 'ts/hotel/classes/FilterModel';
import HotelResultConfig from 'components/hotel/Result/HotelResultConfig';
import { getFilterWithPreviousValues } from 'utils/hotel';
import FilterResultLeft from 'components/hotel/Result/Filters/FilterResultLeft';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import { FilterHotelModel } from 'ts/dynamic/classes/DynamicFilterHotel';
import { FocusedMarker, Position } from 'ts/hotel/interfaces/Generic';
import LoaderMain from 'components/shared/LoaderMain';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import ChooseRoomModal from './ChooseRoomModal';
import { isFreeCxlDefault } from 'constants/generic';

interface ChangeHotelProps {
  data: DynamicEventResponse;
  onSelectNewHotel: (res: ChangeHotelResponse) => void;
  onPressBack: () => void;
}

const ChangeHotel: FC<ChangeHotelProps> = ({ data, ...props }) => {
  const { currency, numberOfPax } = useContext(DynamicContext);
  const [urlParams] = useSearchParams();
  const [choosenRoomPrice, setChoosenRoomPrice] = useState(0);
  const [filter, setFilter] = useState<FilterHotelModel>();
  const [focusedHotel, setFocusedHotel] = useState<FocusedMarker | undefined>();
  const [centered, setCentered] = useState<Position | undefined>();
  const [initedMap, setInitedMap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [activeHotel, setActiveHotel] = useState<Hotel | undefined>();
  const [isShownRoomsModal, setIsShownRoomsModal] = useState(false);
  const [hotelFromOHS, setHotelFromOHS] = useState<Hotel | undefined>();

  const [hotels, setHotels] = useState<Hotel[]>();
  const { t } = useTranslation();
  const selectedRoom =
    data.dynamicHotel?.selectedRoom || data.dynamicHotel?.selectedHotel?.roomClasses[0];

  useEffect(() => {
    if (data.dynamicHotel?.allHotels?.length) {
      const room =
        data.dynamicHotel?.selectedRoom || data.dynamicHotel?.selectedHotel?.roomClasses[0];
      const newChoosenRoomPrice = (room?.price[currency as keyof Price] || 0) / numberOfPax;
      setChoosenRoomPrice(newChoosenRoomPrice);

      let newFitler = new FilterHotelModel(
        data.dynamicHotel?.allHotels || [],
        newChoosenRoomPrice,
        numberOfPax,
        currency,
        !!isFreeCxlDefault
      );
      if (filter) {
        newFitler = getFilterWithPreviousValues(newFitler, filter);
      }

      const initedHotels = initHotels(data?.dynamicHotel?.allHotels || [], urlParams);
      const filteredHotels = getFilteredHotels(
        newFitler,
        initedHotels || [],
        choosenRoomPrice,
        numberOfPax,
        currency
      );

      setFilter(newFitler);
      setHotels(filteredHotels);
    }
  }, [data.dynamicHotel?.allHotels.length]); // eslint-disable-line

  const onChangeHotel = async (hotel: any, room: any) => {
    setIsLoading(true);
    if (
      hotel.htuKey === data.dynamicHotel?.selectedHotel?.htuKey &&
      room.rcUniqueKey === data.dynamicHotel?.selectedRoom?.rcUniqueKey
    ) {
      props.onPressBack();
      setIsLoading(false);
      return;
    }

    const ohsResponse = await ohsForChangeHotel(data.searchToken, hotel, room);

    if (ohsResponse && !ohsResponse.hasErrors && !ohsResponse.oneHotelSearchFailed) {
      const formattedHotel = initSingleHotel(ohsResponse.hotelFromOneHotelSearch, urlParams);
      setHotelFromOHS(formattedHotel);
      setIsShownRoomsModal(true);
    } else if (ohsResponse && ohsResponse.oneHotelSearchFailed) {
      showWarning(t('dyn.there_is_no_any_room_please_choose_another_hotel'));
    } else {
      showError(t('something_went_wrong'));
    }

    setIsLoading(false);
  };

  const onFilterChange = (newFitler: FilterHotelModel) => {
    setFilter(newFitler);
    sessionStorage.setItem('filterHotels', JSON.stringify(newFitler));

    setHotels(getFilteredHotels(newFitler, hotels || [], choosenRoomPrice, numberOfPax, currency));
  };

  const onLocationChange = (newLocation: LocationValue, dontMoveHotels: boolean = false) => {
    if (!newLocation?.lat) return;
    let newHotels = hotels?.map((hotel) => {
      const distance = getDistanceByCoords(
        newLocation.lat || 0,
        newLocation.lng || 0,
        hotel.position.latitude,
        hotel.position.longitude
      );

      return {
        ...hotel,
        distance,
      };
    });

    newHotels?.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));

    setFilter((prevFilter) => {
      const newFilter = {
        ...prevFilter!,
        location: {
          ...prevFilter!.location,
          value: newLocation,
          km: newLocation.km,
          isHotel: newLocation.isHotel,
        },
      };

      if (!dontMoveHotels) {
        setHotels(getFilteredHotels(newFilter, newHotels || [], 20, 2, currency));
      }
      return newFilter;
    });
  };

  const onSortChange = (sort: string) => {
    const newHotels = getSortedHotels(hotels, sort);

    setHotels(newHotels);
  };

  return (
    <MapHotelContext.Provider
      value={{
        setCentered,
        centered,
        focusedHotel,
        setFocusedHotel,
        initedMap,
        setInitedMap,
        currency,
        setCurrency: () => {},
        activeTab,
        setActiveTab,
        activeHotel,
        setActiveHotel,
        pinnedHotelsIds: [],
        setPinnedHotelsIds: () => {},
        geoPolygons: undefined,
        priceMode: '0',
        setPriceMode: () => {},
      }}
    >
      <HotelFilterContext.Provider
        value={{ filter, setFilter, neighborhoodList: [], setNeighborhoodList: () => {} }}
      >
        <div className="hotel-result hotel-result-dynamic">
          <div className="default-shadow" style={{ margin: '10px' }}>
            <Button
              title={t('back')}
              icon={faArrowCircleLeft}
              onClick={props.onPressBack}
              className="btn btn-link"
            />
          </div>
          {!!choosenRoomPrice && (
            <Fragment>
              <div className="hotel-result-header">
                <FilterResults
                  hotels={hotels}
                  onFilterChange={onFilterChange}
                  onSortChange={onSortChange}
                  onLocationChange={onLocationChange}
                  totalHotelsCount={hotels?.length || 0}
                  availableBoardTypes={[]}
                  dynamic={{ currency, choosenRoomPrice, numberOfPax }}
                  sortBy="recommended"
                />
              </div>
              <div className="hotel-result-data">
                <FilterResultLeft onChangeFilter={onFilterChange} />
                <div className="hotel-list-wr">
                  {!!hotels && (
                    <HotelsList
                      hotels={(hotels || [])?.filter((hotel) => hotel.visible) || []}
                      dynamic={{
                        numberOfPax,
                        choosenRoomPrice,
                        currency: currency as string,
                        isLoading,
                      }}
                      goToDetails={({ hotel, room }) => onChangeHotel(hotel, room)}
                    />
                  )}
                </div>

                <HotelResultConfig
                  hotels={hotels}
                  onFilterChange={onFilterChange}
                  onLocationChange={onLocationChange}
                  isMainLoading={isLoading}
                  hideTabs={true}
                  goToDetails={() => {}}
                />
              </div>
            </Fragment>
          )}
          {isShownRoomsModal && hotelFromOHS && (
            <ChooseRoomModal
              roomClasses={hotelFromOHS?.roomClasses || []}
              choosenRoomPrice={
                ((selectedRoom as any).price[data.packageInfo.currencyCode.toLowerCase()] || 0) /
                  numberOfPax || 0
              }
              numberOfPax={numberOfPax}
              selectedRoom={selectedRoom}
              onSelectNewHotel={props.onSelectNewHotel}
              onCloseSuccess={() => {}}
              isLoading={false}
              hotel={hotelFromOHS}
              onClose={() => setIsShownRoomsModal(false)}
              searchToken={data.searchToken}
            />
          )}
        </div>
        
          {isLoading && (
            <LoaderMain systemProducts="Dynamic" systemProductsDetails="LoadingDetails" />
          )}
        
      </HotelFilterContext.Provider>
    </MapHotelContext.Provider>
  );
};

export default withErrorBoundary(ChangeHotel, 'ChangeHotel');
